import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { SectionHeading } from './SectionHeading';
import type { ProseParagraph } from './ProseSection';

/**
 * BLOCK: text_image
 * ACF layout name: `block_text_image`
 *
 * Fields:
 *  - eyebrow        (text)
 *  - heading        (text)
 *  - paragraphs     (repeater: text, style)
 *  - image / alt    (image)
 *  - image_position (select: left | right)
 *  - image_shape    (select: rectangle | circle)
 *  - cta            (group: label, link — optional)
 *
 * Used on: Home ("A space to pause, reconnect and begin again.") and
 * Meet Milangela ("My journey began with a question.").
 */

const PARAGRAPH_CLASS: Record<NonNullable<ProseParagraph['style']>, string> = {
  body: '',
  lead: 'font-serif text-2xl italic text-charcoal',
  accent: 'font-serif text-2xl italic text-sage-700',
  statement: 'font-serif text-xl italic text-charcoal'
};
interface TextImageSectionProps {
  eyebrow?: string;
  heading: string;
  paragraphs: readonly ProseParagraph[];
  image: string;
  imageAlt: string;
  imagePosition?: 'left' | 'right';
  imageShape?: 'rectangle' | 'circle';
  cta?: {
    label: string;
    to: string;
  };
}
export function TextImageSection({
  eyebrow,
  heading,
  paragraphs,
  image,
  imageAlt,
  imagePosition = 'right',
  imageShape = 'rectangle',
  cta
}: TextImageSectionProps) {
  const imageFirst = imagePosition === 'left';
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    amount: 0.15
  }} transition={{
    duration: 0.5,
    ease: 'easeOut'
  }} className={`mx-auto grid max-w-6xl items-center gap-12 lg:gap-20 ${imageFirst ? 'lg:grid-cols-[0.75fr_1.25fr]' : 'lg:grid-cols-2'}`} data-id="element-215" data-mp="2imvu5wvi1vxn">
      <div className={imageFirst ? 'order-1' : 'order-2 lg:order-1'} data-id="element-216" data-mp="1yrpgxfy4abg">
        {imageFirst ? <ImageFigure src={image} alt={imageAlt} shape={imageShape} data-id="element-217" data-mp="6w1kcsm3te6o" /> : <TextColumn eyebrow={eyebrow} heading={heading} paragraphs={paragraphs} cta={cta} data-id="element-218" data-mp="5h79rtz9wo61" />}
      </div>

      <div className={imageFirst ? 'order-2' : 'order-1 lg:order-2'} data-id="element-219" data-mp="3rn4bcwgqznay">
        {imageFirst ? <TextColumn eyebrow={eyebrow} heading={heading} paragraphs={paragraphs} cta={cta} data-id="element-220" data-mp="3nm27g2bvyn2n" /> : <ImageFigure src={image} alt={imageAlt} shape={imageShape} data-id="element-221" data-mp="o8rpvwi7z42k" />}
      </div>
    </motion.div>;
}
function TextColumn({
  eyebrow,
  heading,
  paragraphs,
  cta
}: Pick<TextImageSectionProps, 'eyebrow' | 'heading' | 'paragraphs' | 'cta'>) {
  return <div data-id="element-222" data-mp="aer0vuh2kvfe">
      <SectionHeading eyebrow={eyebrow} heading={heading} data-id="element-223" data-mp="af0gqp8ehm7d" />
      <div className="mt-8 max-w-2xl space-y-5 text-base font-light leading-relaxed text-charcoal-50" data-id="element-224" data-mp="16xeu6csknpf3">
        {paragraphs.map(paragraph => <p key={paragraph.text} className={PARAGRAPH_CLASS[paragraph.style ?? 'body']} data-id="element-225" data-mp="124bynz48i5w9">
            {paragraph.text}
          </p>)}
      </div>
      {cta && <Link to={cta.to} className="group mt-9 inline-flex items-center gap-3 border-b border-charcoal pb-1 text-xs font-medium tracking-[0.16em] text-charcoal uppercase transition-colors hover:border-sage-700 hover:text-sage-700 focus:outline-none focus:ring-2 focus:ring-sage-700 focus:ring-offset-4" data-id="element-226" data-mp="2pudpt106sf1k">
          {cta.label}
          <ArrowRight size={16} strokeWidth={1.5} className="transition-transform group-hover:translate-x-1" aria-hidden="true" data-id="element-227" data-mp="38q3skqfy1fgi" />
        </Link>}
    </div>;
}
function ImageFigure({
  src,
  alt,
  shape
}: {
  src: string;
  alt: string;
  shape: 'rectangle' | 'circle';
}) {
  if (shape === 'circle') {
    return <div className="mx-auto w-full max-w-[340px] lg:max-w-none" data-id="element-228" data-mp="3rgtv2e1lr08u">
        <div className="relative aspect-square" data-id="element-229" data-mp="3duwgm2cb8q8k">
          <div aria-hidden="true" className="absolute -inset-3 rounded-full border border-sage/60 sm:-inset-5" data-id="element-230" data-mp="3dtlcu2iv7ihg" />
          <img src={src} alt={alt} loading="lazy" decoding="async" width={680} height={680} className="relative h-full w-full rounded-full object-cover object-[62%_center] shadow-soft" data-id="element-231" data-mp="3nkps73cgd5ct" />
        </div>
      </div>;
  }
  return <div className="relative mx-auto max-w-xl" data-id="element-232" data-mp="2pxgxqiuh4lyo">
      <div aria-hidden="true" className="absolute -left-4 -top-4 h-full w-full border border-sage/60 sm:-left-6 sm:-top-6" data-id="element-233" data-mp="2pys2olznnzrd" />
      <img src={src} alt={alt} loading="lazy" decoding="async" width={900} height={1020} className="relative h-[390px] w-full object-cover object-[48%_14%] shadow-soft sm:h-[510px] sm:object-[48%_12%]" data-id="element-234" data-mp="2zulnesnj2zk8" />
    </div>;
}